// ─── Recovery Calculator — Pure Logic ────────────────────────────────────────
// South African subscription industry averages.
// ARPU (average revenue per user / month) and failure rates derived from
// PASA dispute data, BankservAfrica 2024 figures and segment-specific market research.

export const ARPU = {
  'B2B SaaS':       1200,
  'ISP / Fibre':     875,
  'Property Mgmt':  9500,  // avg rent/levy per unit
  'Gym & Fitness':   499,
  'Other':           800,
};

export const FAILURE_RATE = {
  'B2B SaaS':       0.06,  //  6% — B2B more stable payment methods
  'ISP / Fibre':    0.09,  //  9% — mixed consumer/business base
  'Property Mgmt':  0.11,  // 11% — residential cash-flow volatility
  'Gym & Fitness':  0.13,  // 13% — high consumer churn, low commitment
  'Other':          0.08,  //  8% — conservative generic estimate
};

export const RECOVERY_RATE      = 0.55;  // Crown Ledger conservative recovery rate
export const CROWN_LEDGER_FEE   = 0.10;  // 10 % performance fee on recovered revenue

/**
 * Calculate recovery economics for a given business type and subscriber count.
 * @param {string} businessType — one of the keys of ARPU / FAILURE_RATE
 * @param {number} subscriberCount — positive integer
 * @returns {{ monthlyAtRisk, conservativeRecovery, crownLedgerFee, netToClient, annualNetToClient, failureRateUsed, arpuUsed } | null}
 */
export function calculateRecovery(businessType, subscriberCount) {
  if (!businessType || !subscriberCount || subscriberCount < 1) return null;

  const arpu        = ARPU[businessType]         ?? ARPU['Other'];
  const failureRate = FAILURE_RATE[businessType] ?? FAILURE_RATE['Other'];

  const monthlyAtRisk        = subscriberCount * arpu * failureRate;
  const conservativeRecovery = monthlyAtRisk * RECOVERY_RATE;
  const crownLedgerFee       = conservativeRecovery * CROWN_LEDGER_FEE;
  const netToClient          = conservativeRecovery - crownLedgerFee;
  const annualNetToClient    = netToClient * 12;

  return {
    monthlyAtRisk,
    conservativeRecovery,
    crownLedgerFee,
    netToClient,
    annualNetToClient,
    failureRateUsed: failureRate,
    arpuUsed: arpu,
  };
}

/** Format a rand value the way South Africans expect. */
export function formatRand(value) {
  if (value == null || Number.isNaN(value)) return 'R 0';
  if (value >= 1_000_000) return `R ${(value / 1_000_000).toFixed(1)}M`;
  return `R ${Math.round(value).toLocaleString('en-ZA')}`;
}

/** Format a plain integer with SA grouping (e.g. 12 345). */
export function formatInt(value) {
  if (value == null || Number.isNaN(value)) return '';
  return Math.round(value).toLocaleString('en-ZA');
}

/** RFC5322-ish email validation, kept pragmatic. */
export function isValidEmail(s) {
  if (typeof s !== 'string') return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s.trim());
}
